import type { ButtonHTMLAttributes, FC, ReactElement, ReactNode } from 'react';

import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { Text } from '../../text/Text';

import styles from './popupContent.module.css';

interface BlockingModalActionButtonBase {
  click?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type: ButtonVariant;
  buttonAttributes?: Partial<ButtonHTMLAttributes<HTMLButtonElement>>;
}

interface BlockingModalActionWithKey extends BlockingModalActionButtonBase {
  tk: string;
  label?: never;
}

interface BlockingModalActionWithChildren extends BlockingModalActionButtonBase {
  label: ReactNode;
  tk?: never;
}

export type BlockingModalActionButton = BlockingModalActionWithKey | BlockingModalActionWithChildren;

interface BlockingModalContentPropsBase {
  actions?: BlockingModalActionButton[];
  inCurtain?: boolean;
  customTitle?: string;
  titleTk?: string;
}

interface BlockingModalContentPropsWithMessage extends BlockingModalContentPropsBase {
  messageData?: string[];
  messageTk: string;
  messageComponent?: never;
}

interface BlockingModalContentPropsWithComponent extends BlockingModalContentPropsBase {
  messageComponent: ReactNode;
  messageData?: never;
  messageTk?: never;
}

export type BlockingModalContentProps = BlockingModalContentPropsWithMessage | BlockingModalContentPropsWithComponent;

export const BlockingModalContent: FC<BlockingModalContentProps> = ({
  actions,
  customTitle,
  inCurtain,
  messageComponent,
  messageData,
  messageTk,
  titleTk,
}): ReactElement => {
  const mods = useModifiers(
    styles,
    'popupContent',
    {
      inCurtain,
    },
    { withBaseClass: true }
  );

  return (
    <div data-testid="blocking-popup-content" className={mods}>
      {titleTk ? (
        <div data-testid="blocking-popup-title" className={styles.popupContent__title}>
          <L10nText tk={titleTk} />
        </div>
      ) : null}
      {customTitle ? (
        <div data-testid="blocking-popup-title" className={styles.popupContent__title}>
          {customTitle}
        </div>
      ) : null}
      <Text className={styles.popupContent__message}>
        {messageTk ? <L10nText tk={messageTk} dynamicValues={messageData} /> : messageComponent}
      </Text>
      {actions?.length ? (
        <div data-testid="blocking-popup-buttons" className={styles.popupContent__buttons}>
          {actions.map((action, index) => (
            <Button
              key={action.tk || index}
              variant={action.type}
              loading={action.loading}
              disabled={action.disabled}
              onClick={action.click}
              {...action.buttonAttributes}
            >
              {action.tk ? <L10nText tk={action.tk} /> : action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
